// AgendaContext.tsx
import { ReactNode, createContext } from "react";
import useAgenda, { AgendaType, CreateAgendaType, EditAgendaType, ReallocateTerceiroPayload } from "./hooks/useAgenda";

interface AgendaContextProviderProps {
    children: ReactNode;
}

export interface AgendaContextData {
    createAgenda: (createAgendaData: CreateAgendaType) => Promise<CreateAgendaType | null>;
    getAgendaPaged: (
        pageNumber: number,
        EmpresaId?: number,
        pageSize?: number,
        ContratoId?: number
    ) => Promise<AgendaType[] | null>;
    updateAgenda: (editAgendaData: EditAgendaType) => Promise<EditAgendaType | null>;
    deleteAgenda: (agendaId: number) => Promise<AgendaType | null>;
    handleGetAgendaById: (agendaId: number) => Promise<AgendaType | null>;
    setAgenda: any;
    agenda: AgendaType;
    listAgenda: AgendaType[];
    initialListAgenda: AgendaType[];
    setListAgenda: (agenda: AgendaType[]) => void;
    setAgendaPageCount: (count: number) => void;
    agendaPageCount: number;
    downloadAgendaPdfByDate: (date: Date) => Promise<void>;
    downloadAgendaPdfCustom: (filtros: {
        empresaId: number;
        startDate?: string;
        endDate?: string;
        agendaIds?: number[];
        contratoId?: number;
    }) => Promise<void>;
    printAgendaPdfByDate: (date: Date) => Promise<void>;
    printAgendaPdfCustom: (filtros: {
        empresaId: number;
        startDate?: string;
        endDate?: string;
        agendaIds?: number[];
        contratoId?: number;
    }) => Promise<void>;
    downloadAgendaListPdf: (filtros: {
        empresaId: number;
        startDate?: string;
        endDate?: string;
        agendaIds?: number[];
        contratoId?: number;
    }) => Promise<void>;
    printAgendaListPdf: (filtros: {
        empresaId: number;
        startDate?: string;
        endDate?: string;
        agendaIds?: number[];
        contratoId?: number;
    }) => Promise<void>;
    listAllAgenda: (empresaId: number, contratoId?: number) => Promise<AgendaType[] | null>;
    realocarFotografo: (payload: ReallocateTerceiroPayload) => Promise<boolean>;
    checkConflitoHorarioFotografo: (terceiroId: number, startDate: string, endDate: string, agendaId?: number) => Promise<boolean>;
    getAgendasComRejeicao: (params: {
        pageNumber: number;
        pageSize?: number;
        empresaId?: number;
        startDate?: string;
        endDate?: string;
    }) => Promise<AgendaType[] | null>;
    replaceAgendaCursos: (agendaId: number, cursoIds: number[]) => Promise<boolean>;
    addCursoNaAgenda: (agendaId: number, cursoId: number) => Promise<boolean>;
    removeCursoDaAgenda: (agendaId: number, cursoId: number) => Promise<boolean>;
}

const initialState: AgendaContextData = {
    createAgenda: async () => null,
    getAgendaPaged: async () => null,
    updateAgenda: async () => null,
    deleteAgenda: async () => null,
    handleGetAgendaById: async () => null,
    setAgenda: () => { },
    agenda: {
        id: 0,
        description: "",
        location: "",
        responsible: "",
        startDate: "",
        endDate: "",
        beca: "",
        step: 0,
        cursoId: 0,
        cursoName: "",
        tipoAgendaId: 0,
        tipoAgendaName: "",
        empresaId: 0,
        contratoId: null,
        terceiros: [],
    },
    listAgenda: [],
    initialListAgenda: [],
    setListAgenda: () => { },
    setAgendaPageCount: () => { },
    agendaPageCount: 0,
    downloadAgendaPdfByDate: async () => { },
    downloadAgendaPdfCustom: async () => { },
    printAgendaPdfByDate: async () => { },
    printAgendaPdfCustom: async () => { },
    downloadAgendaListPdf: async () => { },
    printAgendaListPdf: async () => { },
    listAllAgenda: async () => null,
    realocarFotografo: async () => false,
    checkConflitoHorarioFotografo: async () => false,
    getAgendasComRejeicao: async () => null,
    replaceAgendaCursos: async () => false,
    addCursoNaAgenda: async () => false,
    removeCursoDaAgenda: async () => false,
};

export const AgendaContext = createContext<AgendaContextData>(initialState);

export default function AgendaContextProvider({ children }: AgendaContextProviderProps) {
    const {
        createAgenda,
        getAgendaPaged,
        updateAgenda,
        deleteAgenda,
        handleGetAgendaById,
        agenda,
        listAgenda,
        initialListAgenda,
        setListAgenda,
        setAgendaPageCount,
        agendaPageCount,
        downloadAgendaPdfByDate,
        downloadAgendaPdfCustom,
        printAgendaPdfByDate,
        printAgendaPdfCustom,
        downloadAgendaListPdf,
        printAgendaListPdf,
        listAllAgenda,
        realocarFotografo,
        checkConflitoHorarioFotografo,
        getAgendasComRejeicao,
        replaceAgendaCursos,
        addCursoNaAgenda,
        removeCursoDaAgenda,
    } = useAgenda();

    return (
        <AgendaContext.Provider
            value={{
                createAgenda,
                getAgendaPaged,
                updateAgenda,
                deleteAgenda,
                handleGetAgendaById,
                setAgenda: () => { },
                agenda,
                listAgenda,
                initialListAgenda,
                setListAgenda,
                setAgendaPageCount,
                agendaPageCount,
                downloadAgendaPdfByDate,
                downloadAgendaPdfCustom,
                printAgendaPdfByDate,
                printAgendaPdfCustom,
                downloadAgendaListPdf,
                printAgendaListPdf,
                listAllAgenda,
                realocarFotografo,
                checkConflitoHorarioFotografo,
                getAgendasComRejeicao,
                replaceAgendaCursos,
                addCursoNaAgenda,
                removeCursoDaAgenda,
            }}
        >
            {children}
        </AgendaContext.Provider>
    );
}
